import { DatabaseConnectionManager } from "../core/database-connection-manager";
import { truncate } from "lodash-es";
import { SQLString } from "../types";
import { objectModule } from "@/modules/object";
import { logger } from "@/modules/logger";
import { DATABASE_CONFIG } from "@/domains/database/core/config";
import { DatabaseNotInitializedError, DatabaseQueryError } from "@/domains/errors/kinds/database";
import { PGliteWithExtensions } from "@/domains/database/utils";

export interface QueryResult<T = unknown> {
  rows: T[];
  affectedRows?: number;
}

export interface QueryParams {
  sql: SQLString;
  params?: unknown[];
}

/**
 * Base repository class that provides common database access functionality.
 * All repositories should extend this class.
 */
export abstract class BaseRepository {
  protected connectionManager: DatabaseConnectionManager;
  protected repositoryName: string;

  constructor(connectionManager: DatabaseConnectionManager) {
    if (!connectionManager) {
      throw new Error(`[${this.constructor.name}] ConnectionManager is required`);
    }
    this.connectionManager = connectionManager;
    this.repositoryName = this.constructor.name;
  }

  /**
   * Get database instance with automatic initialization check
   */
  protected get db(): PGliteWithExtensions {
    if (!this.connectionManager.isReady()) {
      throw new DatabaseNotInitializedError({ repository: this.repositoryName });
    }

    return this.connectionManager.getDatabase();
  }

  protected getDbIfReady(): PGliteWithExtensions | null {
    if (!this.connectionManager.isReady()) {
      return null;
    }

    return this.connectionManager.getDatabase();
  }

  /**
   * Execute a database query with error handling and logging
   */
  protected async query<T = unknown>(queryParams: QueryParams): Promise<QueryResult<T>> {
    const { sql, params } = queryParams;

    try {
      const result = await this.db.query(sql, params);
      return result as QueryResult<T>;
    } catch (error) {
      const queryError = new DatabaseQueryError(
        (error as Error)?.message ?? "Unknown database error",
        {
          repository: this.repositoryName,
          sql: truncate(sql, { length: DATABASE_CONFIG.MAX_SQL_LOG_LENGTH }),
          ...objectModule.safeAsJson({ params }),
        }
      );

      logger.error({
        message: `[${this.repositoryName}] Query failed`,
        info: {
          error: queryError.message,
          sql: truncate(sql, { length: DATABASE_CONFIG.MAX_SQL_LOG_LENGTH }),
          ...objectModule.safeAsJson({ params }),
        },
      });

      throw queryError;
    }
  }
}
